import React, { useCallback, useEffect, useMemo, useState, Fragment, useRef } from 'react';
import xor from 'lodash/xor';
import { useRelayEnvironment, graphql, useFragment } from 'react-relay';
import memoizeOne from 'memoize-one';
import { FieldTypeIcon } from '@jira/project-settings__all-icons/src/allIcons.tsx';
import useDebouncedCallback from '@atlassian/jira-platform-use-debounce/src/utils/use-debounce-callback/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import {
	type AsyncLoadOptions,
	MetaActions,
	type SelectedValue,
	type SelectOption,
	type SelectValue,
	type OnChangeWithData,
} from '@atlassian/jira-jql-builder-basic-picker/src/common/types.tsx';
import { getSecondaryLabelWithIcon } from '@atlassian/jira-jql-builder-basic-picker/src/common/utils/get-secondary-label-with-icon.tsx';
import { isSelectOptionArray } from '@atlassian/jira-jql-builder-basic-picker/src/common/utils/is-select-option-array.tsx';
import { isSingleValueOption } from '@atlassian/jira-jql-builder-basic-picker/src/common/utils/is-single-value-option.tsx';
import { usePreviousWithInitial } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';
import {
	ContextualAnalyticsData,
	fireUIAnalytics,
	INLINE_DIALOG,
	useAnalyticsEvents,
	type Attributes,
} from '@atlassian/jira-product-analytics-bridge';
import type { FormatMessage } from '@atlassian/jira-shared-types/src/general.tsx';
import { MERGED_COLUMN_IDS } from '@atlassian/jira-issue-table-common/src/constants.tsx';
import type {
	ColumnConfigWithContainer$key as ColumnsFragment,
	ColumnConfigWithContainer$data as ColumnsData,
} from '@atlassian/jira-relay/src/__generated__/ColumnConfigWithContainer.graphql';
import type { ColumnConfigWithContainer_savedViewId$key as ViewDataFragment } from '@atlassian/jira-relay/src/__generated__/ColumnConfigWithContainer_savedViewId.graphql';
import { MAX_COLUMNS, REMOVE_COLUMNS, ADD_COLUMNS } from '../common/constants.tsx';
import type { CustomComponents } from '../common/types.tsx';
import { isNonNullish } from '../common/utils.tsx';
import { useColumnPickerOpenState } from '../controllers/column-picker-open-state/index.tsx';
import ColumnPicker from './column-picker/main.tsx';
import { useFormatOptionLabelWithShimmer } from './shimmering-label/index.tsx';
import {
	getFieldDisabledMessage,
	isOnlyUnselectableField,
	findNewInsertFields,
	findRemovedFields,
	getUpdatedOptions,
	createOptimisticFieldConfig,
	deleteOptimisticFieldConfig,
	rollbackOptimisticFieldConfig,
	getRollbackFields,
	type Field,
	sortMergedColumnOptions,
} from './utils.tsx';

// on cleanup of fg('jira_inline_field_config_gate'), string [] should be removed
export type onChangeType = (
	args:
		| string[]
		| {
				columnIds: string[];
				onSuccessCallback?: () => void;
				onErrorCallback?: () => void;
				isHorizontalAutoScrollNeeded?: boolean;
				isHorizontalAutoScrollLeftNeeded?: boolean;
				retries?: number;
				analyticsAttributes?: Attributes;
				skipRefetch?: boolean;
		  },
) => void;

export type Props = {
	view?: ViewDataFragment | null;
	/**
	 * Fragment key for a collection of field sets (columns) that should be visible in the issue table. This is
	 * typically obtained by spreading the `main_issueTableColumnPicker_ColumnConfig` fragment in a Relay query,
	 * and passing the data path that corresponds to the place where the fragment is spread in the query.
	 */
	columns: ColumnsFragment;
	/**
	 * Allows consumers to provide custom components to be rendered in certain parts of the column picker.
	 */
	components?: CustomComponents;
	onChange?: onChangeType;
	/**
	 * This callback function is invoked to reset the table columns to their default configuration.
	 * Note: Its presence is used as an indicator to display the "Restore Defaults" button in the column configuration popup.
	 */
	onRestoreDefaultColumns?: () => void;
	/**
	 * Hook that returns the callback to be invoked when options need to be retrieved for the column picker – for
	 * example, when opening the picker, searching for columns, or clicking the “Show more” button to load more columns.
	 *
	 * The first two arguments for this callback are the search term and the pagination cursor, which the consumer can
	 * use to fetch the relevant data. Once data is available, the consumer needs to invoke the callback provided as
	 * third argument with that data.
	 *
	 * Note: in case of pagination, only the new options need to be returned, and the column picker will merge those
	 * with the existing list of options.
	 */
	useColumnLoader: (
		selectedColumns: SelectValue,
		isIFCEnabled?: boolean,
		projectContext?: string,
		savedViewId?: string | null,
	) => AsyncLoadOptions;
	/**
	 * Whether the column picker is in a loading state. This is used to display a loading indicator in the column
	 */
	isLoading?: boolean;
	columnInsertIndex?: number;
	isInBetweenColumnPickerEnabled?: boolean;
	isInlineFieldConfigEnabled?: boolean;
	showInlineFieldCreateOnboarding?: boolean;
	/**
	 * Callback function to be invoked when the mouse hovers over the column picker with isInBetweenColumnPickerEnabled
	 */
	onPickerMouseEnter?: () => void;
	/**
	 * Callback function to be invoked when the mouse leaves the column picker with isInBetweenColumnPickerEnabled
	 */
	onPickerMouseLeave?: () => void;
	/**
	 * Whether the merged column is enabled
	 */
	isMergedColumnEnabled?: boolean;
	projectContext?: string;
};

const mapSelectedColumns = memoizeOne(
	(
		fieldSets: ColumnsData,
		canEditColumnConfiguration: boolean,
		formatMessage: FormatMessage,
	): SelectValue =>
		fieldSets.edges.filter(Boolean).map((edge) => {
			const icon = edge.node.type ? (
				<FieldTypeIcon type={edge.node.type} size="small" />
			) : undefined;
			const fieldTypeDisplayName = edge.node.fieldType?.displayName;

			return {
				optionType: 'option',
				value: edge.node.fieldSetId,
				label: edge.node.displayName,
				secondaryLabel:
					fieldTypeDisplayName && icon
						? getSecondaryLabelWithIcon(icon, fieldTypeDisplayName)
						: undefined,
				isDisabled: !canEditColumnConfiguration,
				isOnlyUnselectable: isOnlyUnselectableField(edge.node.type, canEditColumnConfiguration),
				disabledMessage: getFieldDisabledMessage(edge.node.type, formatMessage),
				...(fg('jira_inline_field_config_gate') && {
					type: edge.node.type,
				}),
			};
		}),
);

const ColumnConfig = ({
	view,
	columns,
	components,
	onChange: onChangeProp,
	onRestoreDefaultColumns: onRestoreDefaultColumnsProp,
	useColumnLoader,
	isLoading = false,
	columnInsertIndex,
	isInBetweenColumnPickerEnabled,
	isMergedColumnEnabled,
	isInlineFieldConfigEnabled,
	showInlineFieldCreateOnboarding,
	onPickerMouseEnter,
	onPickerMouseLeave,
	projectContext,
}: Props) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isColumnUpdateInProgress = useRef(false);

	const environment = useRelayEnvironment();

	// This fragment should be moved to a different file whose responsibility is to provide the savedViewId
	const viewData = useFragment<ViewDataFragment>(
		graphql`
			fragment ColumnConfigWithContainer_savedViewId on JiraIssueSearchViewMetadata
			@argumentDefinitions(
				isSavedListViewTechnicalChangesEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/is-saved-list-view-technical-changes-enabled.relayprovider"
				}
			) {
				savedViewId @include(if: $isSavedListViewTechnicalChangesEnabled)
			}
		`,
		view ?? null,
	);

	const columnsData = useFragment<ColumnsFragment>(
		graphql`
			fragment ColumnConfigWithContainer on JiraIssueSearchFieldSetConnection {
				__id
				totalCount @required(action: THROW)
				edges @required(action: THROW) {
					node @required(action: THROW) {
						fieldSetId @required(action: THROW)
						type @required(action: THROW)
						displayName @required(action: THROW)
						fieldType {
							displayName
						}
					}
				}
			}
		`,
		columns,
	);

	const canEditColumnConfiguration = onChangeProp !== undefined;

	const analyticsAttributes = useMemo(() => {
		const columnPickerNumberOfColumns = columnsData.totalCount;
		const columnPickerFieldTypesSet = new Set<string>();
		columnsData.edges
			.map((edge) => edge?.node.type)
			.filter(Boolean)
			.forEach(columnPickerFieldTypesSet.add, columnPickerFieldTypesSet);
		const columnPickerFieldTypes = Array.from(columnPickerFieldTypesSet).sort();

		return {
			columnPickerNumberOfColumns,
			columnPickerFieldTypes,
			pickerType: 'columnConfiguration',
		};
	}, [columnsData.edges, columnsData.totalCount]);

	const [selectedColumns, setSelectedColumns] = useState<SelectValue>(() =>
		mapSelectedColumns(columnsData, canEditColumnConfiguration, formatMessage),
	);

	const mappedColumnsDataRef = useRef(
		mapSelectedColumns(columnsData, canEditColumnConfiguration, formatMessage),
	);

	const prevColumnsDataRef = useRef(columnsData);
	if (prevColumnsDataRef.current !== columnsData && fg('jira_inline_field_config_gate')) {
		mappedColumnsDataRef.current = mapSelectedColumns(
			columnsData,
			canEditColumnConfiguration,
			formatMessage,
		);
		prevColumnsDataRef.current = columnsData;
	}

	const selectedColumnsBeforeUpdateRef = useRef(mappedColumnsDataRef.current);
	let backupFieldsRef: React.MutableRefObject<Field[]> | undefined;
	if (fg('jira_inline_field_config_gate')) {
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		backupFieldsRef = useRef(getRollbackFields({ environment, fieldConfigId: columnsData.__id }));
	}

	const [{ isOpen }, { open, close }] = useColumnPickerOpenState();
	const prevIsOpen = usePreviousWithInitial(isOpen);

	useEffect(() => {
		// Recompute selected columns when picker renders in open state as config data may have changed
		if (!prevIsOpen && isOpen) {
			setSelectedColumns(
				mapSelectedColumns(columnsData, canEditColumnConfiguration, formatMessage),
			);
		}
	}, [canEditColumnConfiguration, columnsData, formatMessage, isOpen, prevIsOpen]);

	const [debouncedSetSelectedColumns] = useDebouncedCallback(
		(
			options: SelectOption[],
			isHorizontalAutoScrollNeeded?: boolean,
			onSuccessCallback?: () => void,
			onErrorCallback?: () => void,
			isHorizontalAutoScrollLeftNeeded?: boolean,
		) => {
			const columnIds = options
				.map((option: SelectOption) => (isSingleValueOption(option) ? option.value : undefined))
				.filter(Boolean);

			if (fg('jira_inline_field_config_gate')) {
				const attributes = {
					isInline: columnInsertIndex !== undefined,
					actionType:
						options.length < selectedColumnsBeforeUpdateRef.current.length
							? REMOVE_COLUMNS
							: ADD_COLUMNS,
				};

				onChangeProp &&
					onChangeProp({
						columnIds,
						onSuccessCallback: () => {
							onSuccessCallback?.();
							selectedColumnsBeforeUpdateRef.current = mappedColumnsDataRef.current;
							if (backupFieldsRef) {
								backupFieldsRef.current = getRollbackFields({
									environment,
									fieldConfigId: columnsData.__id,
								});
							}
						},
						onErrorCallback: () => {
							onErrorCallback?.();
							if (backupFieldsRef) {
								rollbackOptimisticFieldConfig({
									environment,
									fieldConfigId: columnsData.__id,
									fields: backupFieldsRef?.current,
								});
							}
							setSelectedColumns(selectedColumnsBeforeUpdateRef.current);
						},
						isHorizontalAutoScrollNeeded,
						isHorizontalAutoScrollLeftNeeded,
						analyticsAttributes: attributes,
					});
			} else {
				onChangeProp && onChangeProp(columnIds);
			}
			isColumnUpdateInProgress.current = false;
		},
		1000,
	);

	const handleOnChangeOld = useCallback(
		(options: SelectedValue) => {
			if (isSelectOptionArray(options)) {
				setSelectedColumns(options);
				debouncedSetSelectedColumns(options);
			}
		},
		[debouncedSetSelectedColumns, setSelectedColumns],
	);

	const handleOnChange = useCallback(
		(options: SelectedValue, onSuccessCallback?: () => void, onErrorCallback?: () => void) => {
			isColumnUpdateInProgress.current = true;
			if (!isSelectOptionArray(options)) return;
			if (mappedColumnsDataRef.current.length >= options.length) {
				const removedFields = findRemovedFields(options, mappedColumnsDataRef.current);
				deleteOptimisticFieldConfig({
					environment,
					fieldConfigId: columnsData.__id,
					fields: removedFields,
				});
				const newOptions =
					isMergedColumnEnabled && fg('jira_list_merged_column_gate')
						? sortMergedColumnOptions(options)
						: options;
				setSelectedColumns(newOptions);
				debouncedSetSelectedColumns(newOptions, false, onSuccessCallback, onErrorCallback, false);
				return;
			}
			const insertFields = findNewInsertFields(options, mappedColumnsDataRef.current);

			const fields: Field[] = [];
			insertFields.forEach((option) => {
				if (isSingleValueOption(option)) {
					const { label, value, type } = option;
					fields.push({
						fieldSetId: value,
						name: label,
						type,
						customWidth: option.customWidth,
					});
				}
			});
			createOptimisticFieldConfig({
				environment,
				fieldConfigId: columnsData.__id,
				fields,
				createIndex: columnInsertIndex,
			});

			let isHorizontalAutoScrollLeftNeeded = false;
			let updatedOptions = getUpdatedOptions(
				mappedColumnsDataRef.current,
				insertFields,
				columnInsertIndex,
			);
			if (isMergedColumnEnabled && fg('jira_list_merged_column_gate')) {
				updatedOptions = sortMergedColumnOptions(updatedOptions);

				isHorizontalAutoScrollLeftNeeded = !!fields.find(({ fieldSetId }) =>
					MERGED_COLUMN_IDS.includes(fieldSetId),
				);
			}

			setSelectedColumns(updatedOptions);
			debouncedSetSelectedColumns(
				updatedOptions,
				columnInsertIndex === undefined && !isHorizontalAutoScrollLeftNeeded,
				onSuccessCallback,
				onErrorCallback,
				isHorizontalAutoScrollLeftNeeded,
			);
		},
		[
			columnInsertIndex,
			columnsData.__id,
			debouncedSetSelectedColumns,
			environment,
			isMergedColumnEnabled,
		],
	);
	const handleOnOpen = useCallback(() => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'viewed',
			actionSubject: 'screen',
		});

		const attributes: Attributes = {
			actionType: 'open',
		};

		fireUIAnalytics(analyticsEvent, 'ninDefaultsColumnPickerDialog', attributes);

		open();
	}, [createAnalyticsEvent, open]);

	const onChange: OnChangeWithData = useCallback(
		(options, meta, _, onSuccessCallback, onErrorCallback) => {
			if (
				meta.action === MetaActions.selectOption &&
				isSelectOptionArray(options) &&
				options.length > MAX_COLUMNS
			) {
				return;
			}
			if (fg('jira_inline_field_config_gate')) {
				handleOnChange(options, onSuccessCallback, onErrorCallback);
			} else {
				handleOnChangeOld(options);
			}

			let mergedFieldsCount = 0;
			if (Array.isArray(options) && fg('jira_list_merged_column_gate')) {
				options?.forEach((option: SelectOption) => {
					if ('value' in option && MERGED_COLUMN_IDS.includes(option.value)) {
						mergedFieldsCount += 1;
					}
				});
			}

			if (meta.action === MetaActions.selectOption || meta.action === MetaActions.deselectOption) {
				fireUIAnalytics(createAnalyticsEvent({}), 'checkbox changed', 'columnPickerField', {
					checkboxChecked: meta.action === MetaActions.selectOption,
					...(fg('jira_inline_field_config_gate') && {
						isInline: !!isInBetweenColumnPickerEnabled,
					}),
					...(meta.option &&
						'value' in meta.option &&
						fg('jira_list_merged_column_gate') && {
							fieldType: meta.option?.value,
						}),
					...(fg('jira_list_merged_column_gate') && {
						isMergedColumnVisible: mergedFieldsCount > 1,
					}),
				});
			}
		},
		[createAnalyticsEvent, handleOnChange, handleOnChangeOld, isInBetweenColumnPickerEnabled],
	);

	const loadOptions = useColumnLoader(
		selectedColumns,
		isInlineFieldConfigEnabled,
		projectContext,
		viewData?.savedViewId,
	);

	const onRestoreDefaults = useCallback(() => {
		onRestoreDefaultColumnsProp && onRestoreDefaultColumnsProp();
		return [];
	}, [onRestoreDefaultColumnsProp]);

	// Compute a set of all fieldSetIds that have been selected/deselected and not yet committed in the BE
	// Remove memoised data when cleaning up jira_inline_field_config_gate
	const nonCommittedFieldSetIds: Set<string> = useMemo(() => {
		const localFieldSetIds = selectedColumns
			.map((option) => ('value' in option ? option.value : undefined))
			.filter(isNonNullish);
		const remoteFieldSetIds = columnsData.edges
			.filter(isNonNullish)
			.map((edge) => edge.node.fieldSetId);

		return fg('jira_inline_field_config_gate')
			? new Set()
			: new Set(xor(localFieldSetIds, remoteFieldSetIds));
	}, [columnsData.edges, selectedColumns]);

	const formatOptionLabel = useFormatOptionLabelWithShimmer(nonCommittedFieldSetIds);

	const conditionalOnrestoreDefaults =
		onRestoreDefaultColumnsProp !== undefined ? onRestoreDefaults : false;

	return (
		<ContextualAnalyticsData
			sourceName="fieldSearch"
			sourceType={INLINE_DIALOG}
			attributes={analyticsAttributes}
		>
			<ColumnPicker
				components={components}
				formatOptionLabel={formatOptionLabel}
				onLoadOptions={loadOptions}
				onChange={onChange}
				onSortOptions={isMergedColumnEnabled ? sortMergedColumnOptions : undefined}
				onRestoreDefaults={conditionalOnrestoreDefaults}
				selectedColumns={selectedColumns}
				isOpen={isOpen}
				onOpen={handleOnOpen}
				onClose={close}
				isLoading={isLoading}
				isInBetweenColumnPickerEnabled={isInBetweenColumnPickerEnabled}
				{...(fg('jira_inline_field_config_gate')
					? { columnsDataId: columnsData.__id, columnInsertIndex }
					: {})}
				showInlineFieldCreateOnboarding={showInlineFieldCreateOnboarding}
				onPickerMouseEnter={onPickerMouseEnter}
				onPickerMouseLeave={onPickerMouseLeave}
			/>
		</ContextualAnalyticsData>
	);
};

const ColumnConfigWithContainer = (props: Props) => {
	const Container = props.components?.Container ?? Fragment;
	return (
		<Container>
			<ColumnConfig {...props} />
		</Container>
	);
};

export default ColumnConfigWithContainer;
